import {
  object,
  string,
  pipe,
  nonEmpty,
  length,
  email,
  maxLength,
  minLength,
  regex,
  optional
} from "valibot";
import { convertFieldName } from "@/lib/utils";
import { ChatMode } from "@/stores/page";
import { type ApiBuildAgentConfig } from "@/types/buildAgent";

export const emailSchema = pipe(
  string(),
  nonEmpty("Please enter your email."),
  email("The email is badly formatted.")
);

export const registerSchema = object({
  email: emailSchema,
  password: pipe(
    string(),
    minLength(8, "Your password is too short."),
    maxLength(30, "Your password is too long."),
    regex(/[a-z]/, "Your password must contain a lowercase letter."),
    regex(/[A-Z]/, "Your password must contain a uppercase letter."),
    regex(/[0-9]/, "Your password must contain a number.")
  )
});

export const loginSchema = object({
  email: emailSchema,
  password: pipe(string()),
  invite_code: optional(string())
});

export const registerVerifySchema = object({
  code: pipe(
    string(),
    nonEmpty("Please enter your code."),
    length(6, "Your code must be 6 digits.")
  )
});

export const forgotPasswordSchema = object({
  email: emailSchema
});

export const forgotPasswordResetSchema = object({
  token: string(),
  new_password: pipe(
    string(),
    minLength(8, "Your password is too short."),
    maxLength(30, "Your password is too long."),
    regex(/[a-z]/, "Your password must contain a lowercase letter."),
    regex(/[A-Z]/, "Your password must contain a uppercase letter."),
    regex(/[0-9]/, "Your password must contain a number.")
  ),
  password_confirm: string()
});

export enum LoginType {
  Sign = "sign",
  Register = "register",
  Invite = "invite"
}

interface Paging {
  page: number;
  limit: number;
  total: number;
}

export enum TENANT_ROLE {
  OWNER = "OWNER",
  ADMIN = "ADMIN",
  MEMBER = "MEMBER"
}

export enum STATUS_CODE {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED"
}

interface Result {
  status: STATUS_CODE;
  message: string;
}

interface Timestamp {
  seconds: number;
  nanos: number;
}

// Auth types
interface RegisterRequest {
  email: string;
}

export interface RegisterResponse {
  redirectUrl: string;
  token: string;
  message: string;
}

interface OAUTHLoginAndRegisterRequest {
  provider: string;
}

export interface OAUTHLoginAndRegisterResponse {
  redirectUrl: string;
  token: string;
}

interface RegisterVerifyRequest {
  email: string;
  code: string;
  token: string;
}

export interface RegisterVerifyResponse {
  redirectUrl: string;
  token: string;
  is_initialized: boolean;
  message: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  invite_code: string;
}

export interface UserInitRequest {
  password: string;
}

export interface UserInitResponse {
  token: string;
  error: string;
  message: string;
}

export interface LoginResponse {
  id: string;
  token: string;
  message: string;
}

export interface LogoutResponse {}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  token: string;
  message: string;
}

export interface ForgotPasswordVerifyRequest {
  email: string;
  code: string;
  token: string;
}

export interface ForgotPasswordVerifyResponse {
  redirectUrl: string;
  is_valid: boolean;
  email: string;
  message: string;
  new_token: string;
}

interface ForgotPasswordResetRequest {
  token: string;
  new_password: string;
  password_confirm: string;
}

export interface ForgotPasswordResetResponse {
  message: string;
}

interface UserAuthRequest {}

interface UserAuthResponse {
  token: string;
}

interface SubscriptionInfo {
  plan: PlanType;
  period: PriceType;
  status: "SUB_ACTIVE";
  start_at: string; // "2025-05-22T07:59:44Z";
  end_at: string; // "2025-06-22T08:59:44Z";
}

// User types
export interface User {
  id: string;
  name: string;
  email: string;
  thumbnail_url: string;
  create_at: string;
  update_at: string;
  subscription_info?: SubscriptionInfo;
}

interface UserGetRequest {}

interface UserGetResponse {
  user: User;
}

interface UserAvatarUpdateRequest {}

interface UserAvatarUpdateResponse {
  thumbmail_url: string;
}

interface UserUpdateRequest {
  name: string;
  password: string;
}

interface UserUpdateResponse {
  user: User;
}

// Tenant types
interface CustomConfig {
  remove_webapp_brand: boolean;
  replace_webapp_logo: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  plan: string;
  status: string;
  role: TENANT_ROLE;
  in_trial: boolean;
  trial_end_reason: string;
  custom_config: CustomConfig;
  is_current: boolean;
  create_at: string;
}

interface UserTenantListRequest {
  page: number;
  limit: number;
}

export interface UserTenantListResponse {
  tenants: TenantInfo[];
  page: Paging;
}

export enum TeamMemberRole {
  REGULAR = "regular",
  ADMIN = "admin",
  OWNER = "owner"
}

export enum TeamMemberStatus {
  ACTIVE = "normal",
  PENDING = "pending",
  INVITED = "invited"
}

export interface TeamMember {
  email: string;
  role: TeamMemberRole;
  role_description: string;
  status: TeamMemberStatus;
  joined_at: string;
}

interface UserTenantCurrentGetRequest {}

interface UserTenantCurrentGetResponse {
  tenant: TenantInfo;
}

interface UserTenantSwitchRequest {
  tenant_id: string;
}

interface UserTenantSwitchResponse {
  tenant_info: TenantInfo;
}

export interface TenantMember {
  id: string;
  name: string;
  email: string;
  avatar_url: string;
  role: TENANT_ROLE;
  role_description: string;
  status: string;
  last_login_at: Timestamp;
  created_at: Timestamp;
  joined_at: Timestamp;
}

interface UserTenantMemberListRequest {}

interface UserTenantMemberListResponse {
  members: TenantMember[];
  page: Paging;
}

interface UserTenantMemberInviteByEmailRequest {
  emails: string[];
  role: TENANT_ROLE;
}

interface UserTenantMemberInviteResult {
  status: STATUS_CODE;
  email: string;
  message: string;
  redirectUrl: string;
}

interface UserTenantMemberInviteByEmailResponse {
  rets: UserTenantMemberInviteResult[];
}

interface UserTenantMemberCancelInviteRequest {
  user_id: string;
}

interface UserTenantMemberCancelInviteResponse {}

interface UserTenantMemberUpdateRoleRequest {
  user_id: string;
  role: TENANT_ROLE;
}

interface UserTenantMemberUpdateRoleResponse {
  ret: Result;
}

interface BantCriterion {
  question: string;
}

export interface Condition {
  condition: string;
  value: string;
  points: number;
}

export interface Criteria {
  name: string;
  question: string;
  conditions: Condition[];
  max_points: number;
  open?: boolean; // only save FE
}

export interface QualifyLeadConfig {
  business_type: string;
  contact_fields: {
    required_fields: string[];
    optional_fields: string[];
    use_form: boolean;
  };
  scoring_rules: {
    max_score: number;
    criteria_type: string; // "BANT";
    default_criteria: null | Criteria[];
    custom_criteria: null | Criteria[];
  };
  lead_levels: {
    hot_leads: {
      score_range: number[]; // [80, 100];
      actions: string[]; // ["Update Attribute to HubSpot"];
    };
    warm_leads: {
      score_range: number[]; // [50, 79];
      actions: string[]; // ["Update Attribute to HubSpot"];
    };
    cold_leads: {
      score_range: number[]; // [0, 49];
      actions: string[]; // ["Update Attribute to HubSpot"];
    };
  };
}

interface AppearanceConfigSection {
  name: string;
  welcome_message: {
    message: string;
    popup_mode: boolean;
  };
  conversation_starter: {
    messages: string[] | null; // ai_nav can have null messages
    popup_mode: boolean;
  };
  display_form_immediately: boolean;
  brand_logo: {
    url: string;
  };
  color_theme: {
    primary_color: string;
  };
  disclaim_text: string;
}

export interface AppearanceConfig {
  [ChatMode.Widget]: AppearanceConfigSection;
  [ChatMode.Nav]: AppearanceConfigSection;
}

export interface AdvancedConfig {
  agent_description: string;
  reply_tone: string;
  multi_modal_support: boolean;
  multi_lingual_support: boolean;
}

interface Intention {
  name: string;
  condition: string;
  action: string;
}

interface IntentionConfig {
  intentions: Intention[];
}

export interface AgentConfig {
  id: string;
  agent_id: string;
  config: ApiBuildAgentConfig;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string with timezone
  error: string;
}

export interface AgentImportRequest {
  template_id: string;
  knowledge_base_ids?: Record<string, string>;
  name: string;
  description: string;
  update_kb: boolean;
  tenant_id: string;
  user_id: string;
}

export enum AgentStatus {
  ALIVE = "live",
  DRAFT = "draft"
}

export interface Agent {
  agent_id: string;
  created_at: number; //1744774643;
  description: string;
  knowledge_base_ids?: Record<string, string>;
  name: string;
  status: AgentStatus;
  type: string;
  updated_at: number;
  error: string;
}

export interface AgentInfo {
  id: string;
  name: string;
  type: string;
  description: string;
  status: AgentStatus;
  tenant_id: string;
  user_id: string;
  created_at: number;
  updated_at: number;
  image: string;
  error: string;
  knowledge_base_ids?: Record<string, string>;
}

export interface ChatFile {
  type: string;
  transfer_method: string;
  url: string;
}

export interface ChatRequest {
  agent_id: string;
  external_user_id: string;
  tenant_id: string;
  message_content: string;
  response_mode: string; // blocking/streaming
  conversation_id?: string;
  inputs?: Record<string, string | any>; // optional key-value pairs
  files?: ChatFile[];
}

export interface StreamMessageEvent {
  event: string;
  message_id?: string;
  conversation_id?: string;
  answer?: string;
  created_at?: number;
}

export interface BlockingResponse {
  message_id: string;
  answer: string;
  conversation_id: string;
  created_at: number;
}

export interface ChatResponse {
  response: {
    blocking?: BlockingResponse;
    streaming?: StreamMessageEvent;
  };
}

export enum ConversationType {
  Text = "text",
  Voice = "voice"
}

export interface Conversation {
  agent_id: string;
  conversation_id: string;
  conversation_unique_id?: string;
  created_at: string;
  ended_at: string;
  external_conversation_id?: string;
  lead_level: LeadLevel;
  name: string;
  platform_agent_id?: string;
  started_at: string;
  type: ConversationType;
  updated_at: string;
  voice_agent_id?: string;
  voice_agent_type?: string;
}

export interface ConversationMessage {
  conversation_id: string;
  query: string;
  answer: string;
  created_at: number;
  error?: boolean;
  isLoading?: boolean;
  lastDate?: number; // 距离上一条消息的时间间距（秒）
}

export interface ConversationMessageResponse {
  messages: {
    limit: number;
    has_more: boolean;
    data: ConversationMessage[];
  };
}

export enum Permission {
  OnlyMe = "only_me",
  AllTeamMembers = "all_team_members",
  PartialMembers = "partial_members"
}

export interface DatabaseSource {
  id: string;
  name: string;
  description: string;
  provider: string;
  permission: Permission;
  data_source_type: null;
  indexing_technique: string;
  app_count: number;
  document_count: number;
  word_count: number;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
  embedding_model: null;
  embedding_model_provider: null;
  embedding_available: null;
  retrieval_model_dict: {
    search_method: number;
    reranking_enable: boolean;
    reranking_mode: boolean;
    reranking_model: { reranking_provider_name: ""; reranking_model_name: "" };
    weights: null;
    top_k: number;
    score_threshold_enabled: boolean;
    score_threshold: null;
  };
  tags: [];
  doc_form: null;
  external_knowledge_info: {
    external_knowledge_id: null;
    external_knowledge_api_id: null;
    external_knowledge_api_name: null;
    external_knowledge_api_endpoint: null;
  };
  external_retrieval_model: {
    top_k: 2;
    score_threshold: 0;
    score_threshold_enabled: null;
  };
  doc_metadata: [];
  built_in_field_enabled: false;
  detail: null | string;
}

export enum IndexingStatus {
  Waiting = "waiting",
  Parsing = "parsing",
  Cleaning = "cleaning",
  Splitting = "splitting",
  Indexing = "indexing",
  Completed = "completed",
  Paused = "paused",
  Error = "error"
}

export const indexingProcess = (statue: IndexingStatus) => {
  return {
    [IndexingStatus.Waiting]: 10,
    [IndexingStatus.Parsing]: 20,
    [IndexingStatus.Cleaning]: 40,
    [IndexingStatus.Splitting]: 60,
    [IndexingStatus.Indexing]: 80
  }[statue];
};

export enum KnowledgeType {
  Document = "Document",
  Website = "Website",
  QA = "Q&A"
}

export type Knowledge = {
  id: string;
  name: string;
  data_source_type: KnowledgeType;
  indexing_status: IndexingStatus;
  created_at: number;
  tokens: number;
  word_count: number;
  hit_count: number;
  dataset_id: string;
  question: string;
  answer: string;
  file_type?: string;
};

export type DocumentMetadata = {
  id: string;
  name: string;
  type: string;
  value: string;
  size: number;
};

export type Document = {
  id: string;
  position: number;
  data_source_type: KnowledgeType;
  data_source_info: {
    upload_file_id: string;
    url: string;
    notion_page_id: string;
    notion_workspace_id: string;
  };
  dataset_process_rule_id: string;
  name: string;
  created_from: "api"; // Literal type since it appears to be constrained
  created_by: string;
  created_at: number;
  tokens: number;
  indexing_status: IndexingStatus; // Could be made into a union type if other values exist
  error: string;
  enabled: boolean;
  disabled_at: number;
  disabled_by: string;
  archived: boolean;
  doc_metadata: DocumentMetadata[];
  size: number; // calculate by doc_metadata in FE
  display_status: "queuing"; // Could be made into a union type if other values exist
  word_count: number;
  hit_count: number;
  doc_form: "text_model"; // Could be made into a union type if other values exist
  content: string;
};

export interface Qa {
  question: string;
  answer: string;
}

export interface DocumentResponse {
  data: Document[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  completed_count: number;
  error_count: number;
}

export interface KnowledgesResponse {
  knowledges: Document[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export type DocumentData = {
  document: Document;
  batch: string;
  detail: null | string;
};

export type DocumentsData = {
  documents: Document[];
  batch: string;
  detail: null | string;
  job_id: string;
  total: number;
  completed_count: number;
  error_count: number;
};

export interface IntentionReq {
  name: string;
  condition: string;
  action: string;
}

export type Intension = IntentionReq & {
  id: string;
  is_enabled: boolean;
};

export interface IntentionsResp {
  agent_id: string;
  intentions: Intension[];
  message: string;
}

export interface LeadQualificationDetail {
  phone: string; // "+1-555-123-4567";
  company: string;
  position: string;
  preferred_contact_method: string;
  budget: string; // "10000-20000";
  timeline: string; //  "3 months";
}

export interface LeadQualificationSummary {
  summary: string;
  need: {
    Need: string;
    Current_Challenge: string;
    Impact: string;
  };
  insights: {
    Buying_Signals: string;
    Risks_Red_Flags: string;
  };
  next_actions: {
    Title: string;
    Value: string;
    "Due Date": string;
  }[];
  alert: string;
}

export enum LeadLevel {
  Hot = "hot",
  Warm = "warm",
  Cold = "cold",
  Unqualified = "unqualified"
}

export interface Attachment {
  id: string;
  field: string;
  name: string;
  size: number;
  content_type: string;
  created_at: string;
}

export interface LeadQualification {
  id: string;
  conversation_id: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  agent_type: string;
  max_score: number;
  lead_level: LeadLevel;
  lead_score: number;
  lead_summary: string;
  details: LeadQualificationDetail;
  created_at: string;
  updated_at: string;
  attachments: Attachment[];
}

export interface SalesforceLead {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  custom_fields: {
    [key: string]: string;
  };
}

export interface HubSpotContact {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  website: string;
  job_title: string;
  lifecyclestage: string;
  custom_fields: {
    [key: string]: string;
  };
}

export interface AgentAction {
  integration: string;
  actions: string[];
  is_connected: boolean;
}

export interface CreateActionResponse {
  action_id: string;
  message: string;
}

export interface DeleteActionResponse {
  message: string;
}

export interface IPagination {
  total: number;
  pageIndex: number;
  pageSize: number;
  has_more?: boolean;
}

export enum PriceType {
  Monthly = "MONTHLY",
  Yearly = "YEARLY"
}

export enum PlanType {
  Free = "FREE",
  Pro = "PRO",
  Business = "BUSINESS",
  CUSTOM = "CUSTOM",
  NEW_USER_TRIAL = "NEW_USER_TRIAL"
}

export const PlanTypeName = {
  [PlanType.Free]: "Free",
  [PlanType.Pro]: "Pro",
  [PlanType.Business]: "Business",
  [PlanType.CUSTOM]: "Custom",
  [PlanType.NEW_USER_TRIAL]: "Free Trial"
};

export interface Product {
  id: string;
  period: PriceType;
  plan: PlanType;
  price: number;
  currency: string;
}

enum AddOnCategory {
  ADDON_UNKNOWN = "ADDON_UNKNOWN",
  ADDON_CONVERSATION = "ADDON_CONVERSATION"
}

export interface AddOnPurchase {
  category: AddOnCategory;
  quantity: number;
}

export interface AddOn {
  category: AddOnCategory;
  description: string;
  period: PriceType;
  title?: string;
  price: number;
}

export enum DealValueRange {
  Under1K = "under_1k",
  "1k_5k" = "1k_5k",
  "5k_25k" = "5k_25k",
  "100k_plus" = "100k_plus"
}

export enum SalesCycleLength {
  Monthly = "immediate",
  "1_4_weeks" = "1_4_weeks",
  "1_month" = "1_month",
  "1_3_months" = "1_3_months",
  "3_plus_months" = "3_plus_months"
}

export enum SalesCurrency {
  USD = "USD",
  "CNY" = "CNY",
  "EUR" = "EUR",
  "GBP" = "GBP",
  "JPY" = "JPY"
}

export interface BrandKit {
  id?: number;
  agent_id?: string;
  brandfetch_id?: string;
  name: string;
  domain: string;
  claimed: boolean;
  description: string;
  longDescription: string;
  links: { name: string; url: string }[];
  appearance_config: AppearanceConfig;
  sales_config: {
    typical_deal_value: DealValueRange;
    sales_cycle_length: SalesCycleLength;
    deal_value_currency: SalesCurrency;
    ideal_customer_profile: string[];
    geographic_focus: string[];
    decision_maker_titles: string[];
    segments: string[];
    verticals: string[];
  };
  error?: string;
  logos: {
    theme: string;
    formats: {
      src: string;
      format: string;
      height: number;
      width: number;
      size: number;
      background: string;
    }[];
    tags: string[];
    type: string;
  }[];
  colors: {
    hex: string;
    type: string;
    brightness: number;
  }[];
  fonts?: {
    name: string;
    type: string;
    origin: string;
    originId: string;
    weights: string[];
  }[];
  images: {
    formats: {
      src: string;
      format: string;
      height: number;
      width: number;
      size: number;
      background: string;
    }[];
    tags: string[];
    type: string;
  }[];
  company: {
    employees: number;
    financialIdentifiers: {
      isin: string[];
      ticker: string[];
    };
    foundedYear: number;
    industries: {
      id: string;
      score: number;
      slug: string;
      name: string;
      emoji: string;
      parent: {
        id: string;
        score: number;
        slug: string;
        name: string;
        emoji: string;
        parent: null;
      };
    }[];
    kind: string;
    location: {
      city: string;
      country: string;
      countryCode: string;
      region: string;
      state: string;
      subregion: string;
    };
  };
  qualityScore?: number;
  isNsfw?: boolean;
  url?: string;
  urn?: string;
  created_at?: string;
  updated_at?: string;
  custom_fields: Record<string, any>;
  persona?: string;
}

export const fieldSchema = (field: string) =>
  pipe(string(), nonEmpty(`${convertFieldName(field)} is required.`));

export interface VoiceAgentStrategy {
  name: string;
  is_required: boolean;
  is_selected: boolean;
  question: string;
}

export interface VoiceAgentReq {
  id_enable: boolean;
  voice_mode_id: string;
  language: string;
  strategy: {
    fields: VoiceAgentStrategy[];
  };
  maximum_session_time: number;
  inactivity_check_delay: number;
  max_retries: number;
  inactivity_check_message: string;
}

export interface VoiceAgent {
  id: string;
  platform_agent_id: string;
  is_enabled: boolean;
  voice_mode_id: string;
  language: string;
  strategy: {
    fields: VoiceAgentStrategy[];
  };
  maximum_session_time: number;
  inactivity_check_delay: number;
  max_retries: number;
  inactivity_check_message: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  voice_mode: VoiceMode;
}

export interface VoiceMode {
  id: string;
  name: string;
  gender: number;
  description: string;
  tag: string;
  created_at: string;
  updated_at: string;
}

// Usage-related enums and interfaces based on proto definition
export enum UsageCategory {
  UNKNOWN = "UNKNOWN",
  CONVERSATION = "CONVERSATION",
  AGENT = "AGENT",
  KNOWLEDGE_BASE = "KNOWLEDGE_BASE",
  VOICE_CALL = "VOICE_CALL",
  USAGE_CREDIT = "USAGE_CREDIT",
  USAGE_AI_AGENT = "USAGE_AI_AGENT",
  USAGE_INTEGRATION = "USAGE_INTEGRATION",
  USAGE_DATASET = "USAGE_DATASET"
}

export enum LeadCollectionType {
  LEADCOLLECTION_UNKNOWN = "LEADCOLLECTION_UNKNOWN",
  LEADCOLLECTION_STANDARD_FORM = "LEADCOLLECTION_STANDARD_FORM",
  LEADCOLLECTION_CONVERSATION = "LEADCOLLECTION_CONVERSATION"
}

export enum AnalyticsType {
  ANALYTICS_UNKNOWN = "ANALYTICS_UNKNOWN",
  ANALYTICS_BASIC = "ANALYTICS_BASIC"
}

export enum TrainMatType {
  TRAINMAT_UNKNOWN = "TRAINMAT_UNKNOWN",
  TRAINMAT_WEBSITE = "TRAINMAT_WEBSITE",
  TRAINMAT_DOCUMENT = "TRAINMAT_DOCUMENT",
  TRAINMAT_QA_PAIR = "TRAINMAT_QA_PAIR"
}

export interface UsageInfo {
  category: UsageCategory;
  used_amount: string;
  total_amount: string; // -1 means unlimited
  expiration_time: string; // ISO date string
}

export interface DatasetUsage {
  category: UsageCategory.USAGE_DATASET;
  used_amount: string;
  total_amount: string; // -1 means unlimited
  expiration_time: string; // ISO date string
}

export interface TenantUsage {
  current_plan: PlanType;
  usage_configs: UsageInfo[];
  dataset_usages: {
    [key: string]: DatasetUsage;
  };
  integration_types: string[]; // [ "PLATFORM_CALENDAR", "PLATFORM_HUBSPOT", "PLATFORM_SALESFORCE", "PLATFORM_WEBHOOK"]
  agent_kb_usages: Record<string, UsageInfo>;
  lead_collections: LeadCollectionType[];
  train_material_types: TrainMatType[];
  team_members: number;
  lead_qualification: boolean;
  ai_web_widget: boolean;
  magic_spotlight: boolean;
  multi_lingual: boolean;
  conversation_insights: boolean;
  analytics: AnalyticsType;
  voice_agent: boolean;
  custom_webhook: boolean;
  custom_branding_footer: boolean;
  updated_at: string; // ISO date string
}

export interface IntegrationConnection {
  connection_id: string;
  integration_type: string;
}

export interface IntegrationField {
  field_name: string;
  field_label: string;
  field_type: string;
  field_group: string;
}

export enum IntegrationFieldMappingType {
  Standard = "standard",
  Create_New = "create_new"
}

export interface CreateIntegrationField {
  tenant_id: string;
  agent_id: string;
  source_field_name: string;
  hubspot_field_name: string;
  mapping_type: IntegrationFieldMappingType;
}

export interface IntegrationFieldValue {
  id: number;
  tenant_id: string;
  agent_id: string;
  source_field_name: string;
  hubspot_field_name: string;
  mapping_type: IntegrationFieldMappingType;
  is_active: true;
  created_at: string;
  updated_at: string;
}
export const TotalAmount = {
  [PlanType.Free]: 1 * 1024 * 1024,
  [PlanType.Pro]: 50 * 1024 * 1024,
  [PlanType.NEW_USER_TRIAL]: 50 * 1024 * 1024,
  [PlanType.Business]: 100 * 1024 * 1024
};
